import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input } from "@its-engineering-and-development/ui-foundation/components";

import { Drawer } from "@/components/base/drawer/drawer";
import { LOST_RECEIPT_ESCALATION_LEVEL } from "@app/types/policy";
import type { LostReceiptEscalationLevel, ReceiptPolicyRule, ReceiptRetention } from "@app/types/policy";

import { ReceiptCollectionRules } from "./receipt-collection-rules";
import { LostReceiptProtocol } from "./receipt-lost-protocol";

/** Maximum number of characters allowed for the rule name. */
const RULE_NAME_MAX_LENGTH = 50;

export interface ReceiptPolicyDraft {
    ruleName: string;
    requireReceiptAtOrAbove: string;
    retainReceiptsFor: ReceiptRetention;
    allowMultipleImageUploads: boolean;
    lostReceiptEscalationLevel: LostReceiptEscalationLevel;
}

function toDraft(rule: ReceiptPolicyRule): ReceiptPolicyDraft {
    return {
        ruleName: rule.ruleName,
        requireReceiptAtOrAbove: rule.requireReceiptAtOrAbove,
        retainReceiptsFor: rule.retainReceiptsFor,
        allowMultipleImageUploads: rule.allowMultipleImageUploads,
        lostReceiptEscalationLevel: rule.lostReceiptEscalationLevel,
    };
}

export const BLANK_RECEIPT_DRAFT: ReceiptPolicyDraft = {
    ruleName: "",
    requireReceiptAtOrAbove: "$0",
    retainReceiptsFor: "1 Year",
    allowMultipleImageUploads: false,
    lostReceiptEscalationLevel: LOST_RECEIPT_ESCALATION_LEVEL.Off,
};

export function ReceiptDrawer({
    rule,
    onClose,
    onSave,
    isSaving = false,
    canEdit = true,
}: {
    rule?: ReceiptPolicyRule;
    onClose: () => void;
    onSave: (draft: ReceiptPolicyDraft) => void;
    isSaving?: boolean;
    /** When false, the drawer is read-only: no save action is rendered. */
    canEdit?: boolean;
}) {
    const isCreate = !rule;
    const initialDraft = useMemo(() => (rule ? toDraft(rule) : BLANK_RECEIPT_DRAFT), [rule]);
    const [draft, setDraft] = useState<ReceiptPolicyDraft>(initialDraft);

    useEffect(() => {
        setDraft(rule ? toDraft(rule) : BLANK_RECEIPT_DRAFT);
    }, [rule]);

    const update = useCallback(<K extends keyof ReceiptPolicyDraft>(key: K, value: ReceiptPolicyDraft[K]) => {
        setDraft((prev) => ({ ...prev, [key]: value }));
    }, []);

    const isDirty = useMemo(
        () =>
            draft.ruleName !== initialDraft.ruleName ||
            draft.requireReceiptAtOrAbove !== initialDraft.requireReceiptAtOrAbove ||
            draft.retainReceiptsFor !== initialDraft.retainReceiptsFor ||
            draft.allowMultipleImageUploads !== initialDraft.allowMultipleImageUploads ||
            draft.lostReceiptEscalationLevel !== initialDraft.lostReceiptEscalationLevel,
        [draft, initialDraft],
    );

    const canSave = isDirty && draft.ruleName.trim().length > 0;

    return (
        <Drawer
            title="Receipt Policy Configuration"
            description="Configure receipt requirements, approvals, and travel protocols"
            onClose={onClose}
            isBusy={isSaving}
            footer={
                canEdit ? (
                    <Button
                        color="primary"
                        size="md"
                        onPress={() => onSave(draft)}
                        className="w-full justify-center"
                        isDisabled={!canSave || isSaving}
                        isLoading={isSaving}
                        showTextWhileLoading
                    >
                        {isCreate ? "Create receipt policy" : "Save changes"}
                    </Button>
                ) : undefined
            }
        >
            <Input
                label="Rule Name"
                placeholder="Enter rule name"
                value={draft.ruleName}
                onChange={(val) => update("ruleName", val.slice(0, RULE_NAME_MAX_LENGTH))}
                maxLength={RULE_NAME_MAX_LENGTH}
                isDisabled={!canEdit}
            />
            <ReceiptCollectionRules
                amount={draft.requireReceiptAtOrAbove}
                onAmountChange={(val) => update("requireReceiptAtOrAbove", val)}
                isDisabled={!canEdit}
            />
            <LostReceiptProtocol value={draft.lostReceiptEscalationLevel} onChange={(val) => update("lostReceiptEscalationLevel", val)} isDisabled={!canEdit} />
        </Drawer>
    );
}
